import React from 'react';

/**
 * Service status pill — gentle pastel gradients keyed off the status string.
 *
 * The match is exact-then-substring, case-insensitive, with the trailing-space
 * flavour ("Delivered ") that we have in the live data normalised away.
 *
 * Approved palette (gentle, brand-aligned, professional):
 *
 *   In Queue          amber/yellow
 *   WIP               sky/blue
 *   Test Drive        cyan/teal
 *   Process Invoice   violet/purple
 *   Completed         emerald/teal
 *   Invoice/Invoiced  emerald/green
 *   Delivered         rich green
 *   Warranty          rose/pink
 *   Not Started       slate
 *   Cancelled         stone
 *   PDI               indigo/purple
 *   _fallback_        indigo/purple
 *
 * Use anywhere a status appears in a table:
 *   <StatusPill value={job.doc_type} />
 */

// Exact-match table (lowercase keys). Order is significant only for the
// substring fallback below.
const PALETTE = {
  'in queue':         { grad: 'from-amber-50 to-yellow-100',   text: 'text-amber-800',   ring: 'ring-amber-200/70' },
  'wip':              { grad: 'from-sky-50 to-blue-100',       text: 'text-blue-800',    ring: 'ring-blue-200/70' },
  'test drive':       { grad: 'from-cyan-50 to-teal-100',      text: 'text-teal-800',    ring: 'ring-teal-200/70' },
  'process invoice':  { grad: 'from-violet-50 to-purple-100',  text: 'text-purple-800',  ring: 'ring-purple-200/70' },
  'completed':        { grad: 'from-emerald-50 to-teal-100',   text: 'text-emerald-800', ring: 'ring-emerald-200/70' },
  'invoice':          { grad: 'from-emerald-50 to-green-100',  text: 'text-green-800',   ring: 'ring-green-200/70' },
  'invoiced':         { grad: 'from-emerald-50 to-green-100',  text: 'text-green-800',   ring: 'ring-green-200/70' },
  'delivered':        { grad: 'from-green-100 to-emerald-200', text: 'text-green-900',   ring: 'ring-green-300/70' },
  'warranty':         { grad: 'from-rose-50 to-pink-100',      text: 'text-rose-800',    ring: 'ring-rose-200/70' },
  'not started':      { grad: 'from-slate-50 to-slate-100',    text: 'text-slate-700',   ring: 'ring-slate-200/70' },
  'cancelled':        { grad: 'from-stone-100 to-stone-200',   text: 'text-stone-700',   ring: 'ring-stone-300/70' },
  'cancel':           { grad: 'from-stone-100 to-stone-200',   text: 'text-stone-700',   ring: 'ring-stone-300/70' },
  'pdi':              { grad: 'from-indigo-50 to-purple-100',  text: 'text-indigo-800',  ring: 'ring-indigo-200/70' },
  'advance':          { grad: 'from-fuchsia-50 to-pink-100',   text: 'text-fuchsia-800', ring: 'ring-fuchsia-200/70' },
};

const FALLBACK = { grad: 'from-indigo-50 to-purple-100', text: 'text-indigo-800', ring: 'ring-indigo-200/70' };

const resolve = (value) => {
  if (!value) return null;
  const v = String(value).trim().toLowerCase();
  if (PALETTE[v]) return PALETTE[v];
  // Substring matches handle free-form master values
  if (v.includes('queue') || v.includes('pending')) return PALETTE['in queue'];
  if (v.includes('progress') || v.includes('repair')) return PALETTE['wip'];
  if (v.includes('deliver')) return PALETTE['delivered'];
  if (v.includes('invoice')) return PALETTE['invoice'];
  if (v.includes('complete')) return PALETTE['completed'];
  if (v.includes('warrant')) return PALETTE['warranty'];
  if (v.includes('cancel')) return PALETTE['cancelled'];
  return FALLBACK;
};

export function StatusPill({ value, className = '', testId }) {
  if (!value) return <span className="text-slate-400 text-[11.5px]">—</span>;
  const c = resolve(value) || FALLBACK;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-semibold tracking-tight bg-gradient-to-br ${c.grad} ${c.text} ring-1 ${c.ring} shadow-sm whitespace-nowrap ${className}`}
    >
      {String(value).trim()}
    </span>
  );
}

export default StatusPill;
